import { useState, useEffect, useCallback } from 'react';
import { createLogger } from '@/lib/logger';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Award, Lock, Trophy, Dumbbell, Flame, Users, Calendar } from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';
import BadgeCelebration from '@/components/BadgeCelebration';
import { FLAGS } from '@/lib/featureFlags';

const log = createLogger('BADGES');

interface Badge {
  id: string;
  name: string;
  description: string;
  category: string;
  requirement_type: string;
  requirement_value: number;
  tier: string;
  icon_name: string;
  unlocked?: boolean;
  progress?: number;
  unlocked_at?: string;
}

export default function BadgesScreen() {
  const router = useRouter();
  const { profile } = useAuth();
  const { theme } = useTheme();
  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unlocked' | 'locked'>('all');
  const [celebrationBadge, setCelebrationBadge] = useState<Badge | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);

  // V1: Badges disabled — redirect to home
  useEffect(() => {
    if (!FLAGS.V1_BADGES_ENABLED) {
      router.replace('/(tabs)/home');
    }
  }, [router]);

  useEffect(() => {
    loadBadges();
  }, []);

  useFocusEffect(
    useCallback(() => {
      loadBadges();
    }, [profile])
  );

  const loadBadges = async () => {
    if (!profile) return;

    const { data: badgesData } = await supabase
      .from('badges')
      .select('*')
      .order('sort_order', { ascending: true });

    const { data: userBadges } = await supabase
      .from('user_badges')
      .select('badge_id, progress, unlocked_at')
      .eq('user_id', profile.id);

    const unlockedMap = new Map(userBadges?.map(ub => [ub.badge_id, ub]) || []);

    const badgesWithStatus = await Promise.all(
      (badgesData || []).map(async badge => {
        const userBadge = unlockedMap.get(badge.id);
        const unlocked = !!userBadge;
        let progress = 0;

        if (!unlocked) {
          progress = await calculateBadgeProgress(badge);
        }

        return {
          ...badge,
          unlocked,
          progress: unlocked ? 100 : progress,
          unlocked_at: userBadge?.unlocked_at,
        };
      })
    );

    setBadges(badgesWithStatus);
    setLoading(false);
  };

  const calculateBadgeProgress = async (badge: Badge): Promise<number> => {
    if (!profile) return 0;

    let current = 0;
    const required = badge.requirement_value;

    try {
      switch (badge.requirement_type) {
        case 'workout_count':
          const { count: workoutCount } = await supabase
            .from('workout_sessions')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', profile.id);
          current = workoutCount || 0;
          break;

        case 'pr_count':
          const { count: prCount } = await supabase
            .from('pr_entries')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', profile.id);
          current = prCount || 0;
          break;

        case 'duel_wins':
          const { count: duelCount } = await supabase
            .from('duels')
            .select('*', { count: 'exact', head: true })
            .eq('winner_id', profile.id);
          current = duelCount || 0;
          break;

        case 'account_age_days':
          const accountAge = Math.floor(
            (Date.now() - new Date(profile.created_at).getTime()) / (1000 * 60 * 60 * 24)
          );
          current = accountAge;
          break;

        case 'streak_days':
          const { data: streakData } = await supabase
            .from('user_streaks')
            .select('current_streak')
            .eq('user_id', profile.id)
            .maybeSingle();
          current = streakData?.current_streak || 0;
          break;

        case 'lifting_score':
          current = profile.lifting_score || 0;
          break;

        case 'custom_status':
          // For status badges, calculate progress based on the most limiting metric
          const { data: statusWorkouts } = await supabase.rpc('calculate_workouts_logged', {
            p_user_id: profile.id,
          });

          // Get all metrics needed for status calculation
          const statusWorkoutCount = statusWorkouts || 0;
          const { data: statusActiveWeeksData } = await supabase.rpc('calculate_active_weeks', {
            p_user_id: profile.id,
          });
          const statusActiveWeeks = statusActiveWeeksData || 0;

          const { data: statusStreakData } = await supabase
            .from('user_streaks')
            .select('longest_streak')
            .eq('user_id', profile.id)
            .maybeSingle();
          const statusLongestStreak = statusStreakData?.longest_streak || 0;

          const { data: statusVerifiedLiftsData } = await supabase.rpc('calculate_verified_competitive_lifts', {
            p_user_id: profile.id,
          });
          const statusVerifiedLifts = statusVerifiedLiftsData || 0;

          const { data: statusDuelsData } = await supabase.rpc('calculate_duels_completed', {
            p_user_id: profile.id,
          });
          const statusDuelsCompleted = statusDuelsData || 0;

          const statusAccountAgeDays = Math.floor(
            (Date.now() - new Date(profile.created_at).getTime()) / (1000 * 60 * 60 * 24)
          );

          // Calculate progress based on badge requirement_value
          let statusProgress = 0;
          if (required === 1) {
            // Fresh Plates: WL ≥ 1
            statusProgress = Math.min((statusWorkoutCount / 1) * 100, 100);
          } else if (required === 2) {
            // Iron Habit: WL ≥ 25 AND AW ≥ 6
            statusProgress = Math.min(
              Math.min((statusWorkoutCount / 25) * 100, (statusActiveWeeks / 6) * 100),
              100
            );
          } else if (required === 3) {
            // Built Different: WL ≥ 75 AND AW ≥ 16 AND SB ≥ 7
            statusProgress = Math.min(
              Math.min((statusWorkoutCount / 75) * 100, (statusActiveWeeks / 16) * 100, (statusLongestStreak / 7) * 100),
              100
            );
          } else if (required === 4) {
            // Barbell Authority: WL ≥ 150 AND AW ≥ 32 AND VCL ≥ 5
            statusProgress = Math.min(
              Math.min((statusWorkoutCount / 150) * 100, (statusActiveWeeks / 32) * 100, (statusVerifiedLifts / 5) * 100),
              100
            );
          } else if (required === 5) {
            // Forged by Iron: WL ≥ 300 AND AW ≥ 52 AND DC ≥ 10 AND AA ≥ 365
            statusProgress = Math.min(
              Math.min(
                (statusWorkoutCount / 300) * 100,
                (statusActiveWeeks / 52) * 100,
                (statusDuelsCompleted / 10) * 100,
                (statusAccountAgeDays / 365) * 100
              ),
              100
            );
          } else if (required === 6) {
            // DUEL Standard: WL ≥ 500 AND AW ≥ 78 AND VCL ≥ 25 AND DC ≥ 30 AND AA ≥ 730
            statusProgress = Math.min(
              Math.min(
                (statusWorkoutCount / 500) * 100,
                (statusActiveWeeks / 78) * 100,
                (statusVerifiedLifts / 25) * 100,
                (statusDuelsCompleted / 30) * 100,
                (statusAccountAgeDays / 730) * 100
              ),
              100
            );
          }
          return Math.floor(statusProgress);
      }

      return Math.min(Math.floor((current / required) * 100), 99);
    } catch (error) {
      log.error('Error calculating badge progress:', error);
      return 0;
    }
  };

  const getBadgeIcon = (category: string) => {
    switch (category) {
      case 'strength':
        return Dumbbell;
      case 'consistency':
        return Flame;
      case 'social':
        return Users;
      case 'milestone':
      case 'milestones':
        return Calendar;
      case 'status':
        return Trophy;
      default:
        return Award;
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'fresh_plates':
        return '#CD7F32';
      case 'iron_habit':
        return '#FF6B35';
      case 'built_different':
        return '#FFD700';
      case 'barbell_authority':
        return '#E5E4E2';
      case 'forged_by_iron':
        return '#FF4444';
      case 'duel_standard':
        return '#B9F2FF';
      case 'bronze':
        return '#CD7F32';
      case 'silver':
        return '#C0C0C0';
      case 'gold':
        return '#FFD700';
      case 'platinum':
        return '#E5E4E2';
      case 'diamond':
        return '#B9F2FF';
      default:
        return '#666';
    }
  };

  const getTierName = (tier: string) => {
    const tierMap: Record<string, string> = {
      fresh_plates: 'Fresh Plates',
      iron_habit: 'Iron Habit',
      built_different: 'Built Different',
      barbell_authority: 'Barbell Authority',
      forged_by_iron: 'Forged by Iron',
      duel_standard: 'DUEL Standard',
    };
    return tierMap[tier] || tier.charAt(0).toUpperCase() + tier.slice(1);
  };

  const filteredBadges = badges.filter(badge => {
    if (filter === 'all') return true;
    if (filter === 'unlocked') return badge.unlocked;
    if (filter === 'locked') return !badge.unlocked;
    return true;
  });

  const unlockedCount = badges.filter(b => b.unlocked).length;

  const renderBadge = (badge: Badge) => {
    const BadgeIcon = getBadgeIcon(badge.category);
    const tierColor = getTierColor(badge.tier);

    return (
      <View
        key={badge.id}
        style={[
          styles.badgeCard,
          {
            backgroundColor: theme.colors.surface,
            borderColor: badge.unlocked ? tierColor : theme.colors.border,
            opacity: badge.unlocked ? 1 : 0.6,
          },
        ]}>
        <View
          style={[
            styles.badgeIconContainer,
            {
              backgroundColor: badge.unlocked ? tierColor + '20' : theme.colors.surfaceVariant,
              borderColor: tierColor,
            },
          ]}>
          {badge.unlocked ? (
            <BadgeIcon size={32} color={tierColor} />
          ) : (
            <Lock size={32} color={theme.colors.textTertiary} />
          )}
        </View>

        <View style={styles.badgeInfo}>
          <Text style={[styles.badgeName, { color: theme.colors.text }]}>{badge.name}</Text>
          <Text style={[styles.badgeDescription, { color: theme.colors.textSecondary }]}>
            {badge.description}
          </Text>

          <View style={styles.badgeMeta}>
            <View style={[styles.tierBadge, { borderColor: tierColor }]}>
              <Text style={[styles.tierText, { color: tierColor }]}>
                {getTierName(badge.tier)}
              </Text>
            </View>

            {!badge.unlocked && (
              <View style={styles.progressContainer}>
                <View
                  style={[
                    styles.progressBar,
                    { backgroundColor: theme.colors.surfaceVariant },
                  ]}>
                  <View
                    style={[
                      styles.progressFill,
                      { width: `${badge.progress || 0}%`, backgroundColor: tierColor },
                    ]}
                  />
                </View>
                <Text style={[styles.progressText, { color: theme.colors.textTertiary }]}>
                  {badge.progress}%
                </Text>
              </View>
            )}
          </View>
        </View>
      </View>
    );
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <LinearGradient
        colors={[theme.colors.background, theme.colors.surfaceVariant]}
        style={[styles.header, { borderBottomColor: theme.colors.border }]}>
        <View style={styles.headerTop}>
          <View style={{ width: 28 }} />
          <Text style={[styles.headerTitle, { color: theme.colors.accent }]}>
            BADGES & ACHIEVEMENTS
          </Text>
          <View style={{ width: 28 }} />
        </View>

        <TouchableOpacity
          style={[styles.progressionCard, { backgroundColor: theme.colors.accent }]}
          onPress={() => router.push('/progression')}>
          <Trophy size={28} color={theme.colors.text} />
          <View style={styles.progressionContent}>
            <Text style={[styles.progressionTitle, { color: theme.colors.accentForeground }]}>Road to DUEL Standard</Text>
            <Text style={[styles.progressionSubtitle, { color: theme.colors.accentForeground }]}>Track your progression</Text>
          </View>
        </TouchableOpacity>

        <View style={[styles.statsCard, { backgroundColor: theme.colors.surface }]}>
          <Award size={32} color={theme.colors.accent} />
          <View style={styles.statsInfo}>
            <Text style={[styles.statsValue, { color: theme.colors.text }]}>
              {unlockedCount} / {badges.length}
            </Text>
            <Text style={[styles.statsLabel, { color: theme.colors.textSecondary }]}>
              Badges Unlocked
            </Text>
          </View>
        </View>

        <View style={styles.filters}>
          {(['all', 'unlocked', 'locked'] as const).map(f => (
            <TouchableOpacity
              key={f}
              style={[
                styles.filterButton,
                {
                  backgroundColor: filter === f ? theme.colors.accent : theme.colors.surfaceVariant,
                  borderColor: filter === f ? theme.colors.accent : theme.colors.border,
                },
              ]}
              onPress={() => setFilter(f)}>
              <Text
                style={[
                  styles.filterButtonText,
                  { color: filter === f ? theme.colors.text : theme.colors.textSecondary },
                ]}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </LinearGradient>

      {loading ? (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={theme.colors.accent} />
          <Text style={[styles.loadingText, { color: theme.colors.textSecondary }]}>Loading badges...</Text>
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.content}>
          {filteredBadges.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Award size={48} color={theme.colors.textTertiary} />
              <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
                No badges found
              </Text>
            </View>
          ) : (
            filteredBadges.map(renderBadge)
          )}
        </ScrollView>
      )}

      <BadgeCelebration
        badge={celebrationBadge}
        visible={showCelebration}
        onClose={() => {
          setShowCelebration(false);
          setCelebrationBadge(null);
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
  },
  loadingText: {
    fontSize: 14,
    fontWeight: '500',
    marginTop: 8,
  },
  header: {
    paddingTop: 60,
    paddingBottom: 20,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '900',
    letterSpacing: 1,
  },
  statsCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    padding: 16,
    borderRadius: 2,
    marginBottom: 16,
  },
  statsInfo: {
    flex: 1,
  },
  statsValue: {
    fontSize: 24,
    fontWeight: '900',
  },
  statsLabel: {
    fontSize: 14,
    marginTop: 2,
  },
  filters: {
    flexDirection: 'row',
    gap: 8,
  },
  filterButton: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 0,
    borderWidth: 1,
    alignItems: 'center',
  },
  filterButtonText: {
    fontSize: 14,
    fontWeight: '700',
  },
  content: {
    padding: 16,
  },
  badgeCard: {
    flexDirection: 'row',
    borderWidth: 2,
    borderRadius: 2,
    padding: 16,
    marginBottom: 12,
    gap: 16,
  },
  badgeIconContainer: {
    width: 64,
    height: 64,
    borderRadius: 4,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeInfo: {
    flex: 1,
    gap: 8,
  },
  badgeName: {
    fontSize: 16,
    fontWeight: '700',
  },
  badgeDescription: {
    fontSize: 13,
    lineHeight: 18,
  },
  badgeMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 4,
  },
  tierBadge: {
    borderWidth: 1,
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 4,
  },
  tierText: {
    fontSize: 11,
    fontWeight: '700',
  },
  progressContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  progressBar: {
    flex: 1,
    height: 6,
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
  },
  progressText: {
    fontSize: 11,
    fontWeight: '600',
    minWidth: 35,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 60,
    gap: 12,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: '600',
  },
  progressionCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    padding: 20,
    borderRadius: 2,
    marginBottom: 16,
  },
  progressionContent: {
    flex: 1,
  },
  progressionTitle: {
    fontSize: 18,
    fontWeight: '900',
    marginBottom: 4,
  },
  progressionSubtitle: {
    fontSize: 14,
    opacity: 0.9,
  },
});
